"use client";

import { Crisp } from "crisp-sdk-web";
import { useEffect } from "react";

import { useAppContext } from "../../context/AppContext";

type CrispChatWidgetProps = {
  /** Crisp website identifier (Crisp workspace UUID). */
  websiteId: string;
  /** Current UI locale used to preset the Crisp widget language. */
  locale: string;
};

/**
 * Standard Crisp workspace identifier shape (8-4-4-4-12 hex).
 * Used to reject obvious placeholders like 'TODO' / 'xxx' early so the SDK
 * never tries to load with a bogus ID in production.
 */
const CRISP_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Embeds the Crisp chat widget. Intended to be mounted once inside an
 * authenticated layout, alongside <ShellLayout/>.
 *
 * Named `CrispChatWidget` (not `CrispChat`) to avoid shadowing the SDK's
 * internal `CrispChat` class exposed as `Crisp.chat`.
 *
 * Lifecycle:
 *  - On first authenticated mount, Crisp.configure + Crisp.load are called
 *    with the current `locale`. When `locale` changes later (the app's
 *    LanguageSwitcher navigates client-side via router.push, so the tab is
 *    never reloaded), the already-loaded widget is re-localized in place
 *    via `$crisp.push(["config", "locale", ...])` — crisp-sdk-web exposes no
 *    runtime locale setter, but the underlying $crisp queue API does
 *    (GD-41).
 *  - The `locale` prop is normalized to a 2-letter ISO 639-1 code before
 *    being passed to Crisp, so BCP-47 tags like `pt-BR` still resolve to
 *    a Crisp-supported code (`pt`) instead of silently falling back to en.
 *  - User identity (email, nickname, phone) is synced from AppContext as
 *    soon as it becomes available. `Crisp.user.*` calls are safe before
 *    the script is fully loaded — the SDK queues them on `window.$crisp`.
 *  - On logout (auth flips to unauthenticated), the widget is hidden via
 *    Crisp.chat.hide() and the session is cleared via Crisp.session.reset()
 *    so no prior user identity/cookies persist into a later visitor's session
 *    on shared devices. Crisp's iframe is mounted on <body> and does not
 *    unmount with our React tree, so this explicit cleanup is required.
 *  - On re-login in the same tab, the init effect re-shows the widget
 *    (the loader itself is only injected once per tab).
 */
export function CrispChatWidget({ websiteId, locale }: CrispChatWidgetProps) {
  const { auth, userInfo } = useAppContext();

  // Load Crisp the first time the user is authenticated. On later re-runs
  // (locale change via client-side navigation, or logout → login in the
  // same tab) the loader is already injected, so instead of reloading we
  // re-localize the live widget and make sure it is visible.
  useEffect(() => {
    if (!websiteId) return;
    if (!CRISP_ID_PATTERN.test(websiteId)) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          `[CrispChatWidget] websiteId "${websiteId}" does not match the expected Crisp UUID shape. Widget not loaded.`,
        );
      }
      return;
    }
    if (!auth.isAuthenticated) return;

    // Normalize to ISO 639-1 (e.g. "pt-BR" → "pt"); Crisp expects 2-letter
    // codes and silently falls back to English on unrecognized values.
    const crispLocale = locale.slice(0, 2).toLowerCase();

    if (Crisp.isCrispInjected()) {
      // "config locale" re-localizes the loaded chatbox for the current
      // page, so a language switch applies without a hard reload (GD-41).
      // Raw $crisp queue call — crisp-sdk-web has no runtime locale setter.
      // createSingletonIfNecessary mirrors the SDK's own setters: injection
      // can be detected without this tab having created window.$crisp.
      Crisp.createSingletonIfNecessary();
      window.$crisp.push(["config", "locale", [crispLocale]]);
      Crisp.chat.show();
      return;
    }

    Crisp.configure(websiteId, { locale: crispLocale, autoload: false });
    Crisp.load();
  }, [websiteId, locale, auth.isAuthenticated]);

  // Sync identity as AuthHydrator populates / updates the user.
  useEffect(() => {
    if (!userInfo.email) return;

    Crisp.user.setEmail(userInfo.email);

    // Prefer the user's full name; fall back to the email local-part so
    // support agents never see a blank nickname for a known user.
    const fullName = `${userInfo.firstName} ${userInfo.lastName}`.trim();
    const nickname = fullName || userInfo.email.split("@")[0];
    if (nickname) {
      Crisp.user.setNickname(nickname);
    }

    // `userInfo.phone` is normally E.164 from the backend, but Crisp's
    // setPhone throws synchronously ("Invalid phone") on anything it rejects.
    // An unguarded throw here escapes the effect and takes down the whole
    // dashboard shell — including for users who already have malformed phone
    // data persisted (it re-throws on every load). Swallow it: a missing phone
    // in the support widget is harmless. Form validation is the real gate (GD-40).
    if (userInfo.phone) {
      try {
        Crisp.user.setPhone(userInfo.phone);
      } catch {
        // ponytail: never let a bad phone brick the shell
      }
    }
  }, [userInfo.email, userInfo.firstName, userInfo.lastName, userInfo.phone]);

  // On logout, hide the widget and reset the Crisp session so no prior
  // identity persists. Skipped while auth is still loading (initial render).
  useEffect(() => {
    if (auth.isLoading) return;
    if (auth.isAuthenticated) return;
    if (!Crisp.isCrispInjected()) return;

    // reset() re-initializes a fresh anonymous session, which re-shows the
    // launcher by default — hide() has to run last or it gets undone (GD-79).
    Crisp.session.reset(false);
    Crisp.chat.hide();
  }, [auth.isAuthenticated, auth.isLoading]);

  return null;
}
